import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getApiError } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Search, MoreVertical, Edit, Trash2, UserPlus, Mail, Building2, Users, GitBranch, Lock, Eye, EyeOff, LayoutGrid, LayoutList, Sparkles } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';

const TeamPage = () => {
  const { api, isAdmin, isSuperAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showHierarchyDialog, setShowHierarchyDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editUser, setEditUser] = useState(null);
  const [activeTab, setActiveTab] = useState('members');
  const [userToDelete, setUserToDelete] = useState(null);
  const [viewMode, setViewMode] = useState('grid');

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    department: '',
    role: '',
    job_title: '',
    hourly_rate: 0,
    is_active: true,
    manager_id: '',
    employee_id: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [showChangePasswordDialog, setShowChangePasswordDialog] = useState(false);
  const [changePasswordTarget, setChangePasswordTarget] = useState(null);
  const [changePasswordData, setChangePasswordData] = useState({ new_password: '', confirm_password: '' });
  const [showCPNew, setShowCPNew] = useState(false);
  const [showCPConfirm, setShowCPConfirm] = useState(false);

  useEffect(() => {
    fetchUsers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departmentFilter]);

  const fetchUsers = async () => {
    try {
      const params = departmentFilter !== 'all' ? `?department=${departmentFilter}` : '';
      const res = await api.get(`/users${params}`);
      setUsers(res.data);
    } catch (err) {
      toast.error('Failed to fetch team members');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      confirmPassword: '',
      first_name: '',
      last_name: '',
      department: '',
      role: '',
      job_title: '',
      hourly_rate: 0,
      is_active: true,
      manager_id: '',
      employee_id: '',
    });
    setShowPassword(false);
    setShowConfirmPassword(false);
    setEditUser(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!editUser) {
      if (formData.password.length < 8) {
        toast.error('Password must be at least 8 characters');
        return;
      }
      if (!/[!@#$%^&*(),.?":{}|<>]/.test(formData.password)) {
        toast.error('Password must contain at least one special character');
        return;
      }
      if (formData.password !== formData.confirmPassword) {
        toast.error('Passwords do not match');
        return;
      }
    }
    if (formData.hourly_rate === '' || formData.hourly_rate === null || formData.hourly_rate === undefined) {
      toast.error('Hourly Rate is required');
      return;
    }
    if (parseFloat(formData.hourly_rate) < 0) {
      toast.error('Hourly Rate cannot be negative');
      return;
    }
    try {
      const submitData = { ...formData };
      delete submitData.confirmPassword;
      if (!submitData.manager_id) delete submitData.manager_id;

      if (editUser) {
        const updateData = { ...submitData };
        delete updateData.email;
        delete updateData.password;
        await api.put(`/users/${editUser.id}`, updateData);
        toast.success('Team member updated');
      } else {
        await api.post('/users', submitData);
        toast.success('Team member created');
      }
      setShowCreateDialog(false);
      resetForm();
      fetchUsers();
    } catch (err) {
      toast.error(getApiError(err, 'Failed to save team member'));
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (changePasswordData.new_password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(changePasswordData.new_password)) {
      toast.error('Password must contain at least one special character');
      return;
    }
    if (changePasswordData.new_password !== changePasswordData.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    try {
      await api.put(`/users/${changePasswordTarget.id}/password`, {
        new_password: changePasswordData.new_password,
      });
      toast.success(`Password changed for ${changePasswordTarget.first_name} ${changePasswordTarget.last_name}`);
      setShowChangePasswordDialog(false);
      setChangePasswordData({ new_password: '', confirm_password: '' });
    } catch (err) {
      toast.error(getApiError(err, 'Failed to change password'));
    }
  };

  const assignManager = async (userId, managerId) => {
    try {
      await api.put(`/team/hierarchy/assign-manager?user_id=${userId}${managerId ? `&manager_id=${managerId}` : ''}`);
      toast.success('Manager assigned successfully');
      fetchUsers();
    } catch (err) {
      toast.error(getApiError(err, 'Failed to assign manager'));
    }
  };

  const deleteUser = async (userId) => {
    try {
      await api.delete(`/users/${userId}`);
      toast.success('User deleted');
      setUserToDelete(null);
      fetchUsers();
    } catch (err) {
      toast.error(getApiError(err, 'Failed to delete user'));
    }
  };

  const resendInvite = async (targetUser) => {
    try {
      await api.post(`/users/${targetUser.id}/resend-invite`);
      toast.success(`Invite sent to ${targetUser.email}`);
    } catch (err) {
      toast.error(getApiError(err, 'Failed to resend invite'));
    }
  };

  const toggleUserStatus = async (targetUser) => {
    try {
      await api.put(`/users/${targetUser.id}`, { is_active: !targetUser.is_active });
      toast.success(targetUser.is_active ? 'User deactivated' : 'User activated');
      fetchUsers();
    } catch (err) {
      toast.error('Failed to update user status');
    }
  };

  const openEditDialog = (targetUser) => {
    setFormData({
      email: targetUser.email,
      password: '',
      first_name: targetUser.first_name,
      last_name: targetUser.last_name,
      department: targetUser.department,
      role: targetUser.role,
      job_title: targetUser.job_title || '',
      hourly_rate: targetUser.hourly_rate || 0,
      is_active: targetUser.is_active,
      manager_id: targetUser.manager_id || '',
      employee_id: targetUser.employee_id || '',
    });
    setEditUser(targetUser);
    setShowCreateDialog(true);
  };

  const openHierarchyDialog = (targetUser) => {
    setSelectedUser(targetUser);
    setShowHierarchyDialog(true);
  };

  const getRoleBadge = (role) => {
    const styles = {
      super_admin: 'bg-purple-500/10 text-purple-600 dark:text-purple-400',
      admin: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
      manager: 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400',
      team_member: 'bg-green-500/10 text-green-600 dark:text-green-400',
      finance_viewer: 'bg-orange-500/10 text-orange-600 dark:text-orange-400',
      leadership_viewer: 'bg-primary/20 text-primary',
    };
    const labels = {
      super_admin: 'Super Admin', admin: 'Admin', manager: 'Team Manager',
      team_member: 'Team Member', finance_viewer: 'Finance Viewer', leadership_viewer: 'Leadership Viewer',
    };
    return <Badge className={styles[role]}>{labels[role] || role}</Badge>;
  };

  const getDepartmentLabel = (dept) => {
    const labels = {
      engineering: 'Development',
      development: 'Development',
      marketing: 'Marketing',
      design: 'Design',
      project_management: 'Project Management',
      leadership: 'Leadership',
      hr: 'HR',
    };
    return labels[dept] || dept;
  };

  const filteredUsers = users
    .filter(u =>
      `${u.first_name} ${u.last_name}`.toLowerCase().includes(search.toLowerCase()) ||
      u.email.toLowerCase().includes(search.toLowerCase())
    )
    .sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`));

  const getManagerName = (managerId) => {
    const manager = users.find(u => u.id === managerId);
    return manager ? `${manager.first_name} ${manager.last_name}` : null;
  };

  const getDirectReports = (userId) => {
    return users.filter(u => u.manager_id === userId);
  };

  // Build flat hierarchy list with levels
  const buildHierarchyList = () => {
    const result = [];
    const processed = new Set();

    const addUserWithLevel = (user, level) => {
      if (processed.has(user.id)) return;
      processed.add(user.id);
      result.push({ ...user, level });
      const reports = getDirectReports(user.id);
      reports.forEach(report => addUserWithLevel(report, level + 1));
    };

    // Start with users who have no manager
    const topLevel = users.filter(u => !u.manager_id || !users.find(m => m.id === u.manager_id));
    topLevel.forEach(u => addUserWithLevel(u, 0));

    // Add any orphans
    users.forEach(u => {
      if (!processed.has(u.id)) {
        result.push({ ...u, level: 0 });
      }
    });

    return result;
  };

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <UserPlus className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-xl font-semibold">Access Restricted</h2>
          <p className="text-muted-foreground mt-2">Only admins can manage team members</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="team-page">
      {/* Banner */}
      <div className="relative overflow-hidden rounded-2xl border border-border bg-gradient-to-br from-blue-600/10 via-indigo-500/5 to-violet-400/10 p-6">
        <div className="pointer-events-none absolute -top-10 -right-10 w-48 h-48 rounded-full bg-primary/5 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-8 -left-8 w-36 h-36 rounded-full bg-primary/5 blur-2xl" />
        <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
          <div className="flex items-center gap-4">
            <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-background/70 backdrop-blur-sm border border-border flex items-center justify-center shadow-sm">
              <Users className="w-7 h-7 text-primary" />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-0.5">
                <span className="inline-block w-2 h-2 rounded-full bg-primary animate-pulse" />
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-widest">Member Management</span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
                <span className="bg-gradient-to-r from-primary to-violet-500 bg-clip-text text-transparent">Team</span>
              </h1>
              <p className="text-sm text-muted-foreground mt-0.5 flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5 text-primary/60" />
                Manage team members, roles, and approval hierarchy
              </p>
            </div>
          </div>
          <Dialog open={showCreateDialog} onOpenChange={(open) => { setShowCreateDialog(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="gap-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg shadow-lg shadow-primary/25" data-testid="add-member-btn">
              <UserPlus className="w-4 h-4" /> Add Team Member
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editUser ? 'Edit Team Member' : 'Add New Team Member'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>First Name</Label>
                  <Input
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    required
                    placeholder="e.g., John"
                    data-testid="first-name-input"
                  />
                </div>
                <div>
                  <Label>Last Name</Label>
                  <Input
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    required
                    placeholder="e.g., Smith"
                    data-testid="last-name-input"
                  />
                </div>
              </div>

              {!editUser && (
                <>
                  <div>
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      placeholder="e.g., john.smith@company.com"
                      data-testid="email-input"
                    />
                  </div>
                  <div>
                    <Label>Password</Label>
                    <div className="relative">
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        required
                        placeholder="Min. 8 chars + special character"
                        data-testid="password-input"
                        className="pr-10"
                      />
                      <button
                        type="button"
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        onClick={() => setShowPassword(!showPassword)}
                        tabIndex={-1}
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    {formData.password && formData.password.length < 8 && (
                      <p className="text-xs text-destructive mt-1">Must be at least 8 characters</p>
                    )}
                    {formData.password && formData.password.length >= 8 && !/[!@#$%^&*(),.?":{}|<>]/.test(formData.password) && (
                      <p className="text-xs text-destructive mt-1">Must contain at least one special character</p>
                    )}
                  </div>
                  <div>
                    <Label>Confirm Password</Label>
                    <div className="relative">
                      <Input
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={formData.confirmPassword}
                        onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                        required
                        placeholder="Re-enter password"
                        className="pr-10"
                      />
                      <button
                        type="button"
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        tabIndex={-1}
                      >
                        {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                      <p className="text-xs text-destructive mt-1">Passwords do not match</p>
                    )}
                  </div>
                </>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Job Title</Label>
                  <Input
                    value={formData.job_title}
                    onChange={(e) => setFormData({ ...formData, job_title: e.target.value })}
                    placeholder="e.g., Senior Developer"
                  />
                </div>
                <div>
                  <Label>Employee ID</Label>
                  <Input
                    value={formData.employee_id}
                    onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
                    placeholder="e.g., EMP-00001"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Department</Label>
                  <Select value={formData.department} onValueChange={(v) => setFormData({ ...formData, department: v })}>
                    <SelectTrigger data-testid="department-select">
                      <SelectValue placeholder="Select department" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="development">Development</SelectItem>
                      <SelectItem value="marketing">Marketing</SelectItem>
                      <SelectItem value="design">Design</SelectItem>
                      <SelectItem value="project_management">Project Management</SelectItem>
                      <SelectItem value="leadership">Leadership</SelectItem>
                      <SelectItem value="hr">HR</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Role</Label>
                  <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v })}>
                    <SelectTrigger data-testid="role-select">
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="team_member">Team Member</SelectItem>
                      <SelectItem value="manager">Team Manager</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                      {isSuperAdmin && <SelectItem value="super_admin">Super Admin</SelectItem>}
                      <SelectItem value="finance_viewer">Finance Viewer</SelectItem>
                      <SelectItem value="leadership_viewer">Leadership Viewer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Reports To (Manager)</Label>
                <Select value={formData.manager_id || 'none'} onValueChange={(v) => setFormData({ ...formData, manager_id: v === 'none' ? '' : v })}>
                  <SelectTrigger data-testid="manager-select">
                    <SelectValue placeholder="Select manager..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Manager (Top Level)</SelectItem>
                    {users.filter(u => u.id !== editUser?.id).map(u => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.first_name} {u.last_name} ({getDepartmentLabel(u.department)})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Hourly Rate (₹)</Label>
                <Input
                  type="number"
                  min="0"
                  required
                  value={formData.hourly_rate}
                  onChange={(e) => setFormData({ ...formData, hourly_rate: e.target.value === '' ? '' : parseFloat(e.target.value) })}
                  placeholder="e.g. 25"
                />
              </div>

              {editUser && (
                <div className="flex items-center gap-2">
                  <Switch
                    checked={formData.is_active}
                    onCheckedChange={(v) => setFormData({ ...formData, is_active: v })}
                  />
                  <Label>Active Account</Label>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => { setShowCreateDialog(false); resetForm(); }}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="save-member-btn">
                  {editUser ? 'Update' : 'Create'} Member
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Tabs for Members / Hierarchy */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="members" className="gap-2">
            <Users className="w-4 h-4" /> Team Members
          </TabsTrigger>
          <TabsTrigger value="hierarchy" className="gap-2" data-tour="hierarchy-tab">
            <GitBranch className="w-4 h-4" /> Approval Hierarchy
          </TabsTrigger>
        </TabsList>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mt-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search team members..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="team-search"
            />
          </div>
          <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
            <SelectTrigger className="w-[180px]" data-testid="department-filter">
              <Building2 className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Departments</SelectItem>
              <SelectItem value="development">Development</SelectItem>
              <SelectItem value="marketing">Marketing</SelectItem>
              <SelectItem value="design">Design</SelectItem>
              <SelectItem value="project_management">Project Management</SelectItem>
              <SelectItem value="leadership">Leadership</SelectItem>
              <SelectItem value="hr">HR</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex items-center border border-border rounded-md overflow-hidden">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 transition-colors ${viewMode === 'grid' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-muted'}`}
              title="Grid view"
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 transition-colors ${viewMode === 'list' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-muted'}`}
              title="List view"
            >
              <LayoutList className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Team Members Tab */}
        <TabsContent value="members">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
              <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mb-4">
                <Search className="w-7 h-7 text-muted-foreground/50" />
              </div>
              <h3 className="text-base font-semibold text-foreground mb-1">No team members found</h3>
              <p className="text-sm text-muted-foreground max-w-xs">
                {search ? `No members match "${search}". Try a different name or email.` : 'No members match the selected filters.'}
              </p>
              {search && (
                <button onClick={() => setSearch('')} className="mt-4 text-sm text-primary hover:underline font-medium">
                  Clear search
                </button>
              )}
            </div>
          ) : viewMode === 'grid' ? (
            /* ── Grid View ── */
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-tour="team-grid">
              {filteredUsers.map((member) => {
                const actionMenu = (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => openEditDialog(member)}><Edit className="w-4 h-4 mr-2" /> Edit</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openHierarchyDialog(member)}><GitBranch className="w-4 h-4 mr-2" /> Set Manager</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => toggleUserStatus(member)}>{member.is_active ? 'Deactivate' : 'Activate'}</DropdownMenuItem>
                      {(isAdmin || isSuperAdmin) && <DropdownMenuItem onClick={() => resendInvite(member)}><Mail className="w-4 h-4 mr-2" /> Resend Invite</DropdownMenuItem>}
                      {isSuperAdmin && <DropdownMenuItem onClick={() => { setChangePasswordTarget(member); setShowChangePasswordDialog(true); }}><Lock className="w-4 h-4 mr-2" /> Change Password</DropdownMenuItem>}
                      {isSuperAdmin && <DropdownMenuItem onClick={() => setUserToDelete(member)} className="text-destructive"><Trash2 className="w-4 h-4 mr-2" /> Delete</DropdownMenuItem>}
                    </DropdownMenuContent>
                  </DropdownMenu>
                );
                return (
                  <Card key={member.id} className={`card-hover rounded-xl ${!member.is_active ? 'opacity-60' : ''}`} data-testid={`user-card-${member.id}`}>
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0">
                            {member.profile_image ? (
                              <img src={member.profile_image} alt="Profile" className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full bg-primary/10 flex items-center justify-center text-lg font-bold text-primary">
                                {member.first_name[0]}{member.last_name[0]}
                              </div>
                            )}
                          </div>
                          <div>
                            <div className="font-medium">{member.first_name} {member.last_name}</div>
                            <div className="text-sm text-muted-foreground">{member.job_title || 'No title'}</div>
                            {member.employee_id && <div className="text-xs font-mono text-muted-foreground/70 mt-0.5">{member.employee_id}</div>}
                          </div>
                        </div>
                        {actionMenu}
                      </div>
                      <div className="mt-4 space-y-2">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Mail className="w-4 h-4" /><span className="truncate">{member.email}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Building2 className="w-4 h-4" /><span>{getDepartmentLabel(member.department)}</span>
                        </div>
                        {member.manager_id && (
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <GitBranch className="w-4 h-4" /><span>Reports to: {getManagerName(member.manager_id)}</span>
                          </div>
                        )}
                      </div>
                      <div className="mt-4 pt-4 border-t border-border flex items-center justify-between">
                        {getRoleBadge(member.role)}
                        <div className="flex items-center gap-2">
                          {!member.is_active && <Badge variant="outline" className="text-muted-foreground">Inactive</Badge>}
                          {member.hourly_rate > 0 && <span className="text-sm font-mono">₹{member.hourly_rate}/hr</span>}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            /* ── List View ── */
            <div className="rounded-xl border border-border overflow-hidden">
              <div className="hidden lg:grid grid-cols-[2fr_1fr_1fr_120px_80px_40px] gap-4 px-5 py-2.5 bg-muted/60 border-b border-border text-[11px] font-semibold text-muted-foreground uppercase tracking-widest">
                <span>Member</span>
                <span>Department</span>
                <span>Reports To</span>
                <span>Role</span>
                <span>Rate</span>
                <span />
              </div>
              {filteredUsers.map((member) => {
                const actionMenu = (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0">
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => openEditDialog(member)}><Edit className="w-4 h-4 mr-2" /> Edit</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openHierarchyDialog(member)}><GitBranch className="w-4 h-4 mr-2" /> Set Manager</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => toggleUserStatus(member)}>{member.is_active ? 'Deactivate' : 'Activate'}</DropdownMenuItem>
                      {(isAdmin || isSuperAdmin) && <DropdownMenuItem onClick={() => resendInvite(member)}><Mail className="w-4 h-4 mr-2" /> Resend Invite</DropdownMenuItem>}
                      {isSuperAdmin && <DropdownMenuItem onClick={() => { setChangePasswordTarget(member); setShowChangePasswordDialog(true); }}><Lock className="w-4 h-4 mr-2" /> Change Password</DropdownMenuItem>}
                      {isSuperAdmin && <DropdownMenuItem onClick={() => setUserToDelete(member)} className="text-destructive"><Trash2 className="w-4 h-4 mr-2" /> Delete</DropdownMenuItem>}
                    </DropdownMenuContent>
                  </DropdownMenu>
                );
                return (
                  <div key={member.id} className={`px-5 py-3.5 border-b border-border last:border-b-0 hover:bg-muted/40 transition-colors ${!member.is_active ? 'opacity-60' : ''}`} data-testid={`user-card-${member.id}`}>
                    {/* Mobile */}
                    <div className="lg:hidden flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-9 h-9 rounded-full overflow-hidden flex-shrink-0">
                          {member.profile_image ? (
                            <img src={member.profile_image} alt="Profile" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary">
                              {member.first_name[0]}{member.last_name[0]}
                            </div>
                          )}
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium text-sm truncate">{member.first_name} {member.last_name}</div>
                          <div className="text-xs text-muted-foreground truncate">{member.email}</div>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">{getRoleBadge(member.role)}{!member.is_active && <Badge variant="outline" className="text-muted-foreground text-xs">Inactive</Badge>}</div>
                        </div>
                      </div>
                      {actionMenu}
                    </div>
                    {/* Desktop */}
                    <div className="hidden lg:grid grid-cols-[2fr_1fr_1fr_120px_80px_40px] gap-4 items-center">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-9 h-9 rounded-full overflow-hidden flex-shrink-0">
                          {member.profile_image ? (
                            <img src={member.profile_image} alt="Profile" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary">
                              {member.first_name[0]}{member.last_name[0]}
                            </div>
                          )}
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium text-sm truncate">{member.first_name} {member.last_name}</div>
                          <div className="text-xs text-muted-foreground truncate">{member.job_title || member.email}</div>
                          {member.employee_id && <div className="text-xs font-mono text-muted-foreground/60">{member.employee_id}</div>}
                        </div>
                      </div>
                      <div className="text-sm text-muted-foreground">{getDepartmentLabel(member.department)}</div>
                      <div className="text-sm text-muted-foreground truncate">{member.manager_id ? getManagerName(member.manager_id) : <span className="text-muted-foreground/40">—</span>}</div>
                      <div className="flex items-center gap-1.5 flex-wrap">{getRoleBadge(member.role)}{!member.is_active && <Badge variant="outline" className="text-muted-foreground text-xs">Inactive</Badge>}</div>
                      <div className="text-sm font-mono">{member.hourly_rate > 0 ? `₹${member.hourly_rate}/hr` : <span className="text-muted-foreground/40">—</span>}</div>
                      {actionMenu}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Hierarchy Tab */}
        <TabsContent value="hierarchy">
          <Card className="rounded-xl overflow-hidden">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <GitBranch className="w-5 h-5" />
                Organization Hierarchy
              </CardTitle>
            </CardHeader>
            <CardContent className="overflow-hidden">
              <p className="text-sm text-muted-foreground mb-4">
                Team members' timesheets are automatically routed to their manager for approval.
                Click "Set Manager" to change reporting relationships.
              </p>
              <div className="space-y-2">
                {buildHierarchyList().map((member) => {
                  const reports = getDirectReports(member.id);
                  const indent = Math.min(member.level * 12, 36);
                  return (
                    <div
                      key={member.id}
                      className={`flex items-center gap-2 p-3 rounded-lg hover:bg-muted/50 transition-colors ${member.level === 0 ? 'bg-primary/5' : ''}`}
                      style={{ marginLeft: `${indent}px` }}
                    >
                      <div className="w-9 h-9 bg-primary/10 rounded-lg flex items-center justify-center text-sm font-bold text-primary flex-shrink-0">
                        {member.first_name?.[0]}{member.last_name?.[0]}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium word-break">{member.first_name} {member.last_name}</div>
                        <div className="text-xs text-muted-foreground truncate">{member.job_title || member.role?.replace('_', ' ')}</div>
                      </div>
                      <Badge variant="outline" className="text-xs hidden sm:inline-flex flex-shrink-0">
                        {getDepartmentLabel(member.department)}
                      </Badge>
                      {reports.length > 0 && (
                        <Badge className="bg-primary/10 text-primary text-xs flex-shrink-0 hidden sm:inline-flex">
                          {reports.length} reports
                        </Badge>
                      )}
                      {/* Mobile: badge stacked above button */}
                      <div className="flex flex-col items-center gap-1 flex-shrink-0 sm:hidden">
                        {reports.length > 0 && (
                          <Badge className="bg-primary/10 text-primary text-xs">
                            {reports.length} reports
                          </Badge>
                        )}
                        <Button variant="ghost" size="sm" className="px-2 h-7" onClick={() => openHierarchyDialog(member)}>
                          <GitBranch className="w-4 h-4" />
                        </Button>
                      </div>
                      {/* Desktop: button inline */}
                      <Button variant="ghost" size="sm" className="flex-shrink-0 px-2 hidden sm:inline-flex" onClick={() => openHierarchyDialog(member)}>
                        <GitBranch className="w-4 h-4" />
                      </Button>
                    </div>
                  );
                })}
                {users.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
                    <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mb-4">
                      <Users className="w-7 h-7 text-muted-foreground/50" />
                    </div>
                    <h3 className="text-base font-semibold text-foreground mb-1">No team members yet</h3>
                    <p className="text-sm text-muted-foreground max-w-xs">
                      Add your first team member to start building your hierarchy.
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Change Password Dialog (Super Admin) */}
      <Dialog open={showChangePasswordDialog} onOpenChange={(open) => { setShowChangePasswordDialog(open); if (!open) { setChangePasswordData({ new_password: '', confirm_password: '' }); setShowCPNew(false); setShowCPConfirm(false); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-4 h-4" /> Change Password — {changePasswordTarget?.first_name} {changePasswordTarget?.last_name}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleChangePassword} className="space-y-4 mt-4">
            <div>
              <Label>New Password</Label>
              <div className="relative">
                <Input
                  type={showCPNew ? 'text' : 'password'}
                  value={changePasswordData.new_password}
                  onChange={(e) => setChangePasswordData(prev => ({ ...prev, new_password: e.target.value }))}
                  required
                  placeholder="Min. 8 characters"
                  className="pr-10"
                />
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  onClick={() => setShowCPNew(!showCPNew)}
                  tabIndex={-1}
                >
                  {showCPNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {changePasswordData.new_password && changePasswordData.new_password.length < 8 && (
                <p className="text-xs text-destructive mt-1">Must be at least 8 characters</p>
              )}
            </div>
            <div>
              <Label>Confirm New Password</Label>
              <div className="relative">
                <Input
                  type={showCPConfirm ? 'text' : 'password'}
                  value={changePasswordData.confirm_password}
                  onChange={(e) => setChangePasswordData(prev => ({ ...prev, confirm_password: e.target.value }))}
                  required
                  placeholder="Re-enter new password"
                  className="pr-10"
                />
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  onClick={() => setShowCPConfirm(!showCPConfirm)}
                  tabIndex={-1}
                >
                  {showCPConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {changePasswordData.confirm_password && changePasswordData.new_password !== changePasswordData.confirm_password && (
                <p className="text-xs text-destructive mt-1">Passwords do not match</p>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowChangePasswordDialog(false)}>Cancel</Button>
              <Button type="submit" className="bg-primary hover:bg-primary/90 text-primary-foreground">Set Password</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Assign Manager Dialog */}
      <Dialog open={showHierarchyDialog} onOpenChange={setShowHierarchyDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Set Manager for {selectedUser?.first_name} {selectedUser?.last_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-muted-foreground">
              Select who this person reports to. Their timesheets will be routed to their manager for approval.
            </p>
            <Select
              value={selectedUser?.manager_id || 'none'}
              onValueChange={(v) => {
                assignManager(selectedUser?.id, v === 'none' ? null : v);
                setShowHierarchyDialog(false);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select manager..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No Manager (Top Level)</SelectItem>
                {users.filter(u => u.id !== selectedUser?.id).map(u => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.first_name} {u.last_name} ({getDepartmentLabel(u.department)})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete User Confirmation */}
      <AlertDialog open={!!userToDelete} onOpenChange={(open) => !open && setUserToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Team Member?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{userToDelete?.first_name} {userToDelete?.last_name}</strong>? This action cannot be undone and will permanently remove this user and all their data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => deleteUser(userToDelete?.id)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Yes, Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TeamPage;
